/**
 * A player's best rounds, over the profile: score, match level, when, and whether it was a stake. A plain
 * sheet — the data is a list and the page behind it is a profile the player wants to come back to.
 */

import type { BestScore } from '@/net/api';
import { formatScore } from '@/ui/hud';

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
};

let open: HTMLElement | null = null;

export function closeBestScores(): void {
  open?.remove();
  open = null;
}

/** `null` rows: the list could not be read. */
export function openBestScores(rows: readonly BestScore[] | null): void {
  closeBestScores();
  const back = el('div', 'w21-bs-back');
  back.addEventListener('click', (e) => {
    if (e.target === back) closeBestScores();
  });
  const sheet = el('div', 'w21-bs');
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-label', 'Your best scores');

  const head = el('div', 'w21-bs__head');
  const close = el('button', 'w21-bs__x', '✕') as HTMLButtonElement;
  close.type = 'button';
  close.setAttribute('aria-label', 'Close');
  close.addEventListener('click', closeBestScores);
  head.append(el('h3', undefined, 'Best scores'), close);

  const list = el('ol', 'w21-bs__list');
  if (rows === null) list.append(el('li', 'w21-bs__none', 'Could not load scores.'));
  else if (rows.length === 0) list.append(el('li', 'w21-bs__none', 'No rounds yet.'));
  else {
    rows.forEach((r, i) => {
      const li = el('li', 'w21-bs__row');
      const when = new Date(r.at);
      const date = Number.isNaN(when.getTime()) ? '' : when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      li.append(
        el('span', 'w21-bs__n', String(i + 1)),
        el('b', 'w21-bs__score', formatScore(r.score)),
        el('span', 'w21-bs__meta', [r.level ? `LV ${r.level}` : '', r.fee > 0 ? `${formatScore(r.fee)} stake` : '', date].filter(Boolean).join(' · ')),
      );
      list.append(li);
    });
  }
  sheet.append(head, list);
  back.append(sheet);
  document.body.append(back);
  open = back;
  close.focus();
  const onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    document.removeEventListener('keydown', onKey);
    closeBestScores();
  };
  document.addEventListener('keydown', onKey);
}
